import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star, MessageSquare, User } from 'lucide-react';
import { format } from 'date-fns';

/**
 * VehicleReviews Component
 * 
 * Displays reviews for a vehicle on the vehicle detail page.
 */
const VehicleReviews = ({ vehicleId, api, category }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, total_reviews: 0, completed_trips: 0 });
  const [loading, setLoading] = useState(true);

  const isSpecialty = category === 'specialty_asset';
  const nounSingular = isSpecialty ? 'hire' : 'trip';
  const nounPlural = isSpecialty ? 'hires' : 'trips';
  const subjectNoun = isSpecialty ? 'asset' : 'vehicle';

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get(`/vehicles/${vehicleId}/reviews`);
        setReviews(response.data.reviews || []);
        setStats(response.data.stats || { avg_rating: 0, total_reviews: 0, completed_trips: 0 });
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    if (vehicleId) {
      fetchReviews();
    }
  }, [vehicleId, api]);

  if (loading) {
    return (
      <Card className="border-slate-100">
        <CardContent className="p-5">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-[#004B8D]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-100" data-testid="vehicle-reviews-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#004B8D]" />
            Reviews
          </CardTitle>
          
          {/* Stats summary */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-semibold">{stats.avg_rating || '—'}</span>
            </div>
            <span className="text-slate-500">
              {stats.completed_trips} {stats.completed_trips !== 1 ? nounPlural : nounSingular}
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No reviews yet</p>
            <p className="text-slate-400 text-xs mt-1">Be the first to review this {subjectNoun}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div 
                key={review.review_id} 
                className="pb-4 border-b border-slate-100 last:border-0 last:pb-0"
              >
                <div className="flex items-start gap-3">
                  {/* Reviewer avatar */}
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-slate-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Header row */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium text-slate-900 text-sm">
                        {review.reviewer_name?.split(' ')[0] || 'Guest'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {review.created_at && format(new Date(review.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                    
                    {/* Rating stars */}
                    <div className="flex items-center gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          className={`w-4 h-4 ${
                            star <= review.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-slate-200'
                          }`}
                        />
                      ))}
                    </div>
                    
                    {/* Comment */}
                    {review.comment && (
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {review.comment}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * VehicleRatingBadge Component
 * 
 * Compact rating display for vehicle cards and headers.
 */
export const VehicleRatingBadge = ({ rating, tripCount, size = 'md', category }) => {
  const sizeClasses = {
    sm: 'text-xs gap-1',
    md: 'text-sm gap-1.5',
    lg: 'text-base gap-2'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const countLabel = category === 'specialty_asset' ? 'hires' : 'trips';

  return (
    <div className={`flex items-center ${sizeClasses[size]}`}>
      <div className="flex items-center gap-0.5">
        <Star className={`${iconSizes[size]} fill-amber-400 text-amber-400`} />
        <span className="font-semibold text-slate-700">{rating || '—'}</span>
      </div>
      {tripCount !== undefined && (
        <>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">{tripCount} {countLabel}</span>
        </>
      )}
    </div>
  );
};

export default VehicleReviews;
